'use client';

import { Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { ArrowRight, CheckCircleRounded } from '@mui/icons-material';
import {
  Box,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  CascadingHoverMenus,
  CascadingMenuItem,
} from '@prismo-io/design-system';
import { useMe } from '@prismo-io/feature-auth/providers/me';
import { Application, translate } from '@prismo-io/router';
import {
  LanguageCode,
  type LanguageCodeT,
  UserAccessType,
  type UserAccessTypeT,
} from '@prismo-io/schemas';
import { useFragment } from '@prismo-io/utils/client/use-fragment';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { type FC, useMemo } from 'react';

type Props = {
  userUrl: string;
  access: UserAccessTypeT;
  order?: number;
  disabled?: boolean;
  isCompleted?: boolean;
};

export const HelioItem: FC<Props> = (props: Props) => {
  const {
    userUrl,
    access,
    isCompleted = false,
    disabled = false,
    order = 2,
  } = props;

  const { i18n } = useLingui();
  const { locale = LanguageCode.Enum.fr } = i18n;

  const me = useMe();

  const isMe = me && me.url === userUrl;

  const pathname = usePathname();
  const fragment = useFragment();

  const baseUrl = useMemo(
    () =>
      translate({
        app: Application.APP,
        path: '/profil/:user/motivations-helio',
        params: {
          user: userUrl,
        },
        locale: locale as LanguageCodeT,
      }),
    [locale, userUrl]
  );

  const selected = useMemo(() => {
    return pathname.startsWith(baseUrl);
  }, [baseUrl, pathname]);

  return (
    <CascadingHoverMenus
      variant="popover"
      cascadingMenuProps={{
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'right',
        },
      }}
      button={(props) => (
        <MenuItem
          {...props}
          selected={selected}
          component={Link}
          href={baseUrl}
          sx={{ order, paddingY: 2 }}
          disabled={(!isCompleted && !isMe) || disabled}
        >
          <ListItemIcon sx={{ minWidth: 'auto' }}>
            {isCompleted && (
              <CheckCircleRounded color="primary" fontSize="small" />
            )}
            {!isCompleted && (
              <Box
                component="div"
                borderRadius={9999}
                width="24px"
                height="24px"
                display="flex"
                alignItems="center"
                justifyItems="center"
                color="primary.main"
                sx={{
                  borderStyle: 'solid',
                  borderWidth: '2px',
                  borderColor: 'primary.main',
                }}
              >
                <Typography
                  sx={{ flex: 1 }}
                  color="primary"
                  fontSize="small"
                  fontWeight="bolder"
                  textAlign="center"
                >
                  {order}
                </Typography>
              </Box>
            )}
          </ListItemIcon>
          <ListItemText sx={{ flex: 1 }}>
            <Typography display="inline" fontWeight="bolder">
              Helio |{' '}
            </Typography>
            <Typography display="inline">
              <Trans>Motivations</Trans>
            </Typography>
          </ListItemText>
          <ListItemIcon sx={{ minWidth: '12px !important' }}>
            <ArrowRight fontSize="small" />
          </ListItemIcon>
        </MenuItem>
      )}
      popupId="helio-menu"
    >
      <CascadingMenuItem
        component={Link}
        href={`${baseUrl}#helio-1`}
        selected={'#helio-1' === fragment}
        disabled={!isCompleted}
      >
        <Trans>Profil motivationnel</Trans>
      </CascadingMenuItem>

      {access !== UserAccessType.Enum.VISITOR &&
        access !== UserAccessType.Enum.FREE && (
          <CascadingMenuItem
            component={Link}
            href={`${baseUrl}#helio-2`}
            selected={'#helio-2' === fragment}
            disabled={!isCompleted}
          >
            <Trans>Valeurs motivationnelles</Trans>
          </CascadingMenuItem>
        )}
    </CascadingHoverMenus>
  );
};
